import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import rematchService from "../Services/rematchService";
import analytics from "../Services/analyticsService";
import useReplayRoom from "../hooks/useReplayRoom";
import { REPLAY_COPY, INVITE_COPY } from "../utils/hostingCopy";

const CAP_MESSAGE = "You already have 3 lobbies open. Start or leave one first.";

// The play-again part of the final results screen. The host starts it;
// everyone else sees an invitation once it exists.
const RematchPanel = ({ roomData, roomDocId, currentUserId }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const shownFor = useRef(null);

  const rematch = roomData?.rematch;
  const isHost = roomData?.hostId === currentUserId;
  const hostName = rematch?.hostName || roomData?.players?.find((p) => p.id === roomData?.hostId)?.name || "The host";
  const myName =
    localStorage.getItem("playerName") ||
    roomData?.players?.find((p) => p.id === currentUserId)?.name ||
    "";
  const replay = useReplayRoom(rematch?.roomId);
  const alreadyIn = replay.players.some((p) => p.id === currentUserId);

  useEffect(() => {
    if (!isHost && rematch?.roomId && replay.status === "waiting" && !alreadyIn && shownFor.current !== rematch.roomId) {
      shownFor.current = rematch.roomId;
      analytics.track("rematch_invite_shown", { room_id: rematch.roomId });
    }
  }, [isHost, rematch?.roomId, replay.status, alreadyIn]);

  const goToNewGame = (roomId) => {
    rematchService.openLobby({ roomId }, myName, isHost);
    navigate("/create-room");
  };

  const hostTap = async () => {
    if (rematch?.roomId && replay.status !== "gone") {
      goToNewGame(rematch.roomId);
      return;
    }
    setBusy(true);
    setError("");
    const result = await rematchService.startRematch(roomData, roomDocId, myName);
    setBusy(false);
    if (!result.success) {
      setError(result.reason === "lobby_cap" ? CAP_MESSAGE : REPLAY_COPY.error);
      return;
    }
    navigate("/create-room");
  };

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await rematchService.acceptRematch(rematch, myName);
      if (result.success) {
        navigate("/create-room");
        return;
      }
      setError(result.reason === "started" ? INVITE_COPY.started(hostName) : INVITE_COPY.error);
    } catch (e) {
      console.error("Error joining the new game:", e);
      analytics.track("rematch_invite_failed", { room_id: rematch.roomId, reason: "error" });
      setError(INVITE_COPY.error);
    } finally {
      setBusy(false);
    }
  };

  if (isHost) {
    const hasLobby = !!rematch?.roomId && replay.status !== "gone";
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={hostTap}
          disabled={busy}
          className="w-full bg-red-600 text-white font-semibold px-8 py-4 rounded-xl hover:bg-red-700 disabled:opacity-60 transition duration-300 shadow-lg flex items-center justify-center gap-2"
        >
          <RotateCcw size={20} />
          {busy ? REPLAY_COPY.loading : hasLobby ? REPLAY_COPY.existing : REPLAY_COPY.button}
        </button>
        {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      </div>
    );
  }

  if (!rematch) {
    return <p className="mb-4 text-sm text-gray-600">{INVITE_COPY.hint(hostName)}</p>;
  }
  if (replay.status === "loading") return null;

  if (alreadyIn && replay.status !== "gone") {
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={() => goToNewGame(rematch.roomId)}
          className="w-full bg-red-600 text-white font-semibold px-8 py-4 rounded-xl hover:bg-red-700 transition duration-300 shadow-lg flex items-center justify-center gap-2"
        >
          <RotateCcw size={20} />
          {REPLAY_COPY.existing}
        </button>
      </div>
    );
  }

  if (replay.status !== "waiting") {
    return (
      <p role="status" className="mb-4 text-sm text-gray-700 bg-white rounded-xl shadow p-4">
        {INVITE_COPY.started(hostName)}
      </p>
    );
  }

  if (dismissed) return null;

  return (
    <div className="mb-4 bg-white rounded-xl shadow-lg p-5 text-center">
      <p className="font-semibold text-gray-900">{INVITE_COPY.title(hostName)}</p>
      <p className="text-sm text-gray-600 mb-3">{INVITE_COPY.message}</p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="flex-1 bg-red-600 text-white font-semibold px-4 py-3 rounded-lg hover:bg-red-700 disabled:opacity-60"
        >
          {busy ? INVITE_COPY.loading : INVITE_COPY.accept}
        </button>
        <button
          type="button"
          onClick={() => {
            rematchService.declineRematch(rematch);
            setDismissed(true);
          }}
          disabled={busy}
          className="flex-1 bg-gray-100 text-gray-800 font-semibold px-4 py-3 rounded-lg hover:bg-gray-200 disabled:opacity-60"
        >
          {INVITE_COPY.decline}
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
    </div>
  );
};

export default RematchPanel;
